import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Settings.css';
import { changePassword } from '../api';
import logger from '../logger';

const Settings = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const userObj = JSON.parse(storedUser);

        // Full name (firstname + lastname if both exist)
        const displayName =
          userObj.firstname && userObj.lastname
            ? `${userObj.firstname} ${userObj.lastname}`
            : userObj.firstname || userObj.lastname || userObj.email || "User";

        setName(displayName);
        setEmail(userObj.email || "");
      }
    } catch (err) {
      logger.error("Error reading user from localStorage:", err);
    }
  }, []);

  const validateImage = (file, callback) => {
    if (!file) return;
    const validTypes = ['image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG or GIF file.');
      callback(null);
      return;
    }

    if (file.size > 4 * 1024 * 1024) {
      alert('File size must be 4MB or less.');
      callback(null);
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width < 98 || img.height < 98) {
        alert('Image must be at least 98x98 pixels.');
        callback(null);
      } else {
        callback(file);
      }
      URL.revokeObjectURL(img.src);
    };
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    validateImage(file, (validFile) => {
      if (validFile) {
        setLogoFile(URL.createObjectURL(validFile));
        logger.debug('Logo uploaded:', validFile.name);
      }
    });
  };

  const handleProfilePictureUpload = (e) => {
    const file = e.target.files[0];
    validateImage(file, (validFile) => {
      if (validFile) {
        setProfilePictureFile(URL.createObjectURL(validFile));
        logger.debug('Profile picture uploaded:', validFile.name);
      }
    });
  };

  const handleNameEmailSubmit = (e) => {
    e.preventDefault();

    try {
      const storedUser = localStorage.getItem("user");
      let userObj = storedUser ? JSON.parse(storedUser) : {};

      const parts = name.trim().split(" ");
      userObj.firstname = parts[0] || "";
      userObj.lastname = parts.length > 1 ? parts.slice(1).join(" ") : "";
      userObj.email = email;

      localStorage.setItem("user", JSON.stringify(userObj));

      alert("Details updated successfully!");
    } catch (err) {
      logger.error("Error saving user to localStorage:", err);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      setPasswordMessage('Please enter a new password');
      return;
    }

    if (oldPassword && oldPassword === newPassword) {
      setPasswordMessage('New password must be different from old password');
      return;
    }

    try {
      setIsChangingPassword(true);
      setPasswordMessage('');
      await changePassword(newPassword);
      setPasswordMessage('✅ Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setTimeout(() => setPasswordMessage(''), 3000);
    } catch (err) {
      let errorMsg = 'Failed to change password. Please try again.';
      // Handle different error formats
      if (err?.error && typeof err.error === 'string') {
        errorMsg = err.error;
      } else if (err?.message) {
        errorMsg = err.message;
      } else if (err?.errors) {
        // Laravel validation errors
        const firstError = Object.values(err.errors)[0];
        errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
      }
      setPasswordMessage(`❌ ${errorMsg}`);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-content">
        {/* Logo Section */}
        <div className="settings-section">
          <h3>Logo</h3>
          <p className="section-description">
            This image will appear as the app icon and logo
          </p>

          <div className="current-logo">
            <div className="logo-display">
              {logoFile ? (
                <img src={logoFile} alt="Logo" className="logo-image" />
              ) : (
                <>
                  <div className="logo-icon">
                    <span className="logo-e">e</span>
                  </div>
                  <div className="logo-text">
                    <span className="logo-care">Care</span>
                    <span className="logo-elearning">eLearning</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <p className="recommendation-text">
            Recommended: Use a 98x98px image, 4MB or less, in PNG or GIF (no animations).
          </p>

          <label className="btn-change">
            Change Logo
            <input
              type="file"
              accept="image/png,image/gif"
              style={{ display: 'none' }}
              onChange={handleLogoUpload}
            />
          </label>
        </div>

        {/* Profile Picture Section */}
        <div className="settings-section">
          <h3>Profile Picture</h3>
          <p className="section-description">
            This image appears at the top of the admin page
          </p>

          <div className="current-profile-picture">
            <div className="profile-picture-display">
              {profilePictureFile ? (
                <img src={profilePictureFile} alt="Profile" className="profile-image" />
              ) : (
                <div className="profile-avatar">
                  <div className="avatar-woman">
                    <div className="woman-hair"></div>
                    <div className="woman-face"></div>
                    <div className="woman-sweater"></div>
                    <div className="woman-book"></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="recommendation-text">
            Recommended: Use a 98x98px image, 4MB or less, in PNG or GIF (no animations).
          </p>

          <label className="btn-change">
            Change Picture
            <input
              type="file"
              accept="image/png,image/gif"
              style={{ display: 'none' }}
              onChange={handleProfilePictureUpload}
            />
          </label>
        </div>

        {/* Name and Email Section */}
        <div className="settings-section">
          <h3>Name and Email</h3>
          <form className="name-email-form" onSubmit={handleNameEmailSubmit}>
            <div className="form-input">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>
            <div className="form-input">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <button type="submit" className="btn-submit">Update Details</button>
          </form>
        </div>

        {/* Change Password Section */}
        <div className="settings-section">
          <h3>Change Password</h3>
          <form className="password-form" onSubmit={handlePasswordSubmit}>
            <div className="form-input">
              <label htmlFor="oldPassword">Old Password</label>
              <input
                type="password"
                id="oldPassword"
                value={oldPassword}
                onChange={(e) => {
                  setOldPassword(e.target.value);
                  setPasswordMessage('');
                }}
                placeholder="Enter old password"
                required
                disabled={isChangingPassword}
              />
            </div>
            <div className="form-input">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordMessage('');
                }}
                placeholder="Enter new password"
                required
                disabled={isChangingPassword}
              />
            </div>
            {passwordMessage && (
              <div className={`password-message ${passwordMessage.includes('✅') ? 'success' : 'error'}`}>
                {passwordMessage}
              </div>
            )}
            <button
              type="submit"
              className="btn-submit"
              disabled={isChangingPassword}
            >
              {isChangingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Roles & Permissions Section — links out to its own page since the
            role list + permission picker need much more room than this grid gives it. */}
        <div className="settings-section">
          <h3>Roles &amp; Permissions</h3>
          <p className="section-description">
            Create roles and control which permissions each one grants.
          </p>
          <button type="button" className="btn-submit" onClick={() => navigate('/settings/roles')}>
            Manage Roles &amp; Permissions
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
